import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { 
  Entity, 
  EntityCreateRequest, 
  EntityFilterRequest, 
  EntityResponse, 
  EntityStatus, 
  EntityJurisdiction, 
  EntityRegisteredAgent, 
  EntityGoverningCountry, 
  EntityType, 
  EntityGroupAccount,
  EntityDropdown 
} from './entities.model';

@Injectable({
  providedIn: 'root'
})
export class EntitiesService {

  constructor(private readonly http: HttpClient) { }

  // API endpoints
  private readonly entitiesUrl = environment.main_url + '/agilelegal/entities';
  private readonly entityStatusUrl = environment.main_url + '/agilelegal/entity-status';
  private readonly entityJurisdictionUrl = environment.main_url + '/agilelegal/entity-jurisdiction';
  private readonly entityRegisteredAgentUrl = environment.main_url + '/agilelegal/entity-registered-agent';
  private readonly entityGoverningCountryUrl = environment.main_url + '/agilelegal/entity-governing-country';
  private readonly entityTypeUrl = environment.main_url + '/agilelegal/entity-type';
  private readonly entityGroupAccountUrl = environment.main_url + '/agilelegal/entity-group-account';

  /**
   * Get all entities with pagination and filtering
   */
  getEntities(pageNumber?: number, pageSize?: number, payload?: EntityFilterRequest): Observable<EntityResponse<Entity[]>> {
    const params = new URLSearchParams();
    if (pageNumber) params.append('pageNumber', pageNumber.toString());
    if (pageSize) params.append('pageSize', pageSize.toString());
    
    const url = `${this.entitiesUrl}/filter${params.toString() ? '?' + params.toString() : ''}`;
    return this.http.post<EntityResponse<Entity[]>>(url, payload || {});
  }

  /**
   * Get entity by ID
   */
  getEntityById(entityId: number): Observable<EntityResponse<Entity>> {
    return this.http.get<EntityResponse<Entity>>(`${this.entitiesUrl}/${entityId}`);
  }

  /**
   * Get entity by task ID
   */
  getEntityByTaskId(taskId: number): Observable<EntityResponse<Entity>> {
    return this.http.get<EntityResponse<Entity>>(`${this.entitiesUrl}/task/${taskId}`);
  }

  /**
   * Create or update entity
   */
  saveEntity(entity: EntityCreateRequest): Observable<EntityResponse<Entity>> {
    return this.http.post<EntityResponse<Entity>>(this.entitiesUrl, entity);
  }

  /**
   * Get entities for dropdown
   */
  getEntitiesForDropdown(): Observable<EntityResponse<EntityDropdown[]>> {
    return this.http.get<EntityResponse<EntityDropdown[]>>(`${this.entitiesUrl}/fetchEntitiesForDropdown`);
  }

  /**
   * Get entity status options for dropdown
   */
  getEntityStatusForDropdown(): Observable<EntityResponse<EntityStatus[]>> {
    return this.http.get<EntityResponse<EntityStatus[]>>(`${this.entityStatusUrl}/fetchEntityStatusForDropdown`);
  }

  /**
   * Get entity jurisdiction options for dropdown
   */
  getEntityJurisdictionForDropdown(): Observable<EntityResponse<EntityJurisdiction[]>> {
    return this.http.get<EntityResponse<EntityJurisdiction[]>>(`${this.entityJurisdictionUrl}/fetchEntityJurisdictionForDropdown`);
  }

  /**
   * Get entity registered agent options for dropdown
   */
  getEntityRegisteredAgentForDropdown(): Observable<EntityResponse<EntityRegisteredAgent[]>> {
    return this.http.get<EntityResponse<EntityRegisteredAgent[]>>(`${this.entityRegisteredAgentUrl}/fetchEntityRegisteredAgentForDropdown`);
  }

  /**
   * Get entity governing country options for dropdown
   */
  getEntityGoverningCountryForDropdown(): Observable<EntityResponse<EntityGoverningCountry[]>> {
    return this.http.get<EntityResponse<EntityGoverningCountry[]>>(`${this.entityGoverningCountryUrl}/fetchEntityGoverningCountryForDropdown`);
  }

  /**
   * Get entity type options for dropdown
   */
  getEntityTypeForDropdown(): Observable<EntityResponse<EntityType[]>> {
    return this.http.get<EntityResponse<EntityType[]>>(`${this.entityTypeUrl}/fetchEntityTypeForDropdown`);
  }

  /**
   * Get entity group account options for dropdown
   */
  getEntityGroupAccountForDropdown(): Observable<EntityResponse<EntityGroupAccount[]>> {
    return this.http.get<EntityResponse<EntityGroupAccount[]>>(`${this.entityGroupAccountUrl}/fetchEntityGroupAccountForDropdown`);
  }
}